// Neo's sparkle on a phone's home, Alarms, an alarm's page and every board
// page (TOM-547, TOM-817): the same glass button at the bottom right, opening
// the owner's main chat (NeoHomeChat, TOM-517) with the same swoop. Above
// 768 px the chat is a panel down the right side and the page makes room for
// it; at 768 px and under it covers the screen. Home renders its own chat and
// this sparkle (BoatHome); the other pages render SparkleChat.
//
// Open or closed is home's own state, per boat (lib/homeChat), so the chat
// stays open from page to page and on home. The unread dot works as on home.
// While a board is in edit mode, or its Edit chat is open, the sparkle and its
// chat hide: the Edit chat is the chat (sparkle-chat.css).

import { useCallback, useEffect, useRef, useState, type Ref } from 'react';

import { ErrorBoundary } from '../ErrorBoundary';
import { NeoHomeChat } from '../NeoChat';
import { Icon } from '../ui/Icon';
import { readHomeChatOpen, writeHomeChatOpen } from '../../lib/homeChat';
import { PHONE_QUERY, useMediaQuery } from '../../lib/useMediaQuery';

export function SparkleChat({
  boatId,
  suggestions,
}: {
  boatId: string;
  /** The mode's suggested questions, above the chat's message box (TOM-817). */
  suggestions: readonly string[];
}) {
  const phone = useMediaQuery(PHONE_QUERY);
  // Closed on a first visit, then as the owner left it, here or on home.
  // Opening clears the dot; closing gives the focus back to the sparkle,
  // which comes back as the chat swoops off.
  const [open, setOpen] = useState(() => readHomeChatOpen(boatId));
  const [unread, setUnread] = useState(false);
  const sparkleRef = useRef<HTMLButtonElement | null>(null);
  const closedHere = useRef(false);
  const openChat = useCallback(
    (next: boolean) => {
      setOpen(next);
      writeHomeChatOpen(boatId, next);
      if (next) setUnread(false);
      else closedHere.current = true;
    },
    [boatId]
  );
  const markUnread = useCallback(() => setUnread(true), []);
  useEffect(() => {
    setOpen(readHomeChatOpen(boatId));
    setUnread(false);
  }, [boatId]);
  useEffect(() => {
    if (open || !closedHere.current) return;
    closedHere.current = false;
    sparkleRef.current?.focus();
  }, [open]);

  // The page clears the sparkle at its foot while the chat is closed, and
  // makes room for the panel beside it while it is open above 768 px.
  // Over the whole screen on a phone, the page under it is out of reach.
  useEffect(() => {
    const body = document.body;
    body.classList.add('neo-sparkle-page');
    body.classList.toggle('neo-sparkle-open', open && !phone);
    body.classList.toggle('neo-sparkle-full', open && phone);
    const page = document.querySelector<HTMLElement>('.boat-app');
    const wasInert = page?.hasAttribute('inert') ?? false;
    if (open && phone) page?.setAttribute('inert', '');
    return () => {
      body.classList.remove('neo-sparkle-page', 'neo-sparkle-open', 'neo-sparkle-full');
      if (open && phone && !wasInert) page?.removeAttribute('inert');
    };
  }, [open, phone]);

  return (
    <div className="sparkle-chat" data-chat={open ? 'open' : 'closed'}>
      {/* A failure in the chat leaves the page standing. */}
      <ErrorBoundary label="sparkle chat">
        <NeoHomeChat
          key={boatId}
          boatId={boatId}
          open={open}
          fullScreen={phone}
          onOpenChange={openChat}
          onUnread={markUnread}
          boardEditsElsewhere
          suggestions={suggestions}
        />
      </ErrorBoundary>
      {open ? null : (
        <NeoSparkle buttonRef={sparkleRef} unread={unread} onOpen={() => openChat(true)} />
      )}
    </div>
  );
}

/** The round glass sparkle at the bottom right, with a dot when Neo wrote. */
export function NeoSparkle({
  buttonRef,
  unread,
  onOpen,
}: {
  buttonRef: Ref<HTMLButtonElement>;
  unread: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className="tb-icon-btn tb-icon-btn--glass boat-home__sparkle sparkle-chat__button"
      aria-label={unread ? 'Open Neo. Neo has a new message.' : 'Open Neo'}
      title="Neo"
      data-testid="neo-sparkle"
      data-unread={unread ? 'true' : undefined}
      onClick={onOpen}
    >
      <Icon name="sparkles" size={24} />
      {unread ? <span className="boat-home__unread" aria-hidden="true" /> : null}
    </button>
  );
}
